<template>
  <el-dialog
    :visible.sync="dialogVisible"
    custom-class="xsou-auth-dialog"
    width="980px"
    :show-close="false"
    :close-on-click-modal="false"
    append-to-body
    @closed="handleClosed"
  >
    <div class="auth-dialog-shell" :class="festivalThemeClass">
      <aside class="auth-brand-panel">
        <!-- Xsou 供应链场景 -->
        <img
          class="auth-brand-scene"
          src="~@/assets/bg/login.png"
          :alt="$t('auth.brandScene')"
        />
        <div class="auth-brand-shade"></div>
        <div class="auth-brand-content">
          <img
            class="auth-brand-logo"
            :class="{ 'festival-auth-logo': activeDoodle }"
            :src="authLogoSrc"
            :alt="authLogoAlt"
          />
          <div class="auth-brand-copy">
            <!-- 让每一次供需连接 -->
            <!-- 更快、更准、更可靠 -->
            <h2>
              {{ $t("auth.brandHeadlineFirst") }}<br />{{
                $t("auth.brandHeadlineSecond")
              }}
            </h2>
            <p class="auth-brand-values">
              <!-- 卓越服务 -->
              <span>{{ $t("auth.brandService") }}</span>
              <!-- 持续创造 -->
              <span>{{ $t("auth.brandCreation") }}</span>
              <!-- 协作共赢 -->
              <span>{{ $t("auth.brandCollaboration") }}</span>
              <!-- 价值共享 -->
              <span>{{ $t("auth.brandValue") }}</span>
            </p>
          </div>
        </div>
      </aside>

      <section
        class="auth-account-panel"
        :class="{ 'is-registering': primaryMode === 'register' }"
      >
        <!-- 关闭 -->
        <button
          class="auth-close"
          type="button"
          :aria-label="$t('auth.close')"
          @click="closeDialog"
        >
          <span></span><span></span>
        </button>

        <header class="auth-account-header">
          <!-- 账号操作 -->
          <div
            class="auth-primary-tabs"
            role="tablist"
            :aria-label="$t('auth.accountActions')"
          >
            <svg
              v-for="item in primaryTabs"
              :key="`${item.value}-card`"
              class="auth-primary-tabs__card"
              :class="{ active: primaryMode === item.value }"
              viewBox="0 0 1000 140"
              preserveAspectRatio="none"
              aria-hidden="true"
              focusable="false"
            >
              <path
                v-if="item.value === 'login'"
                d="M20 0H502C520 0 522 14 532 48L544 92C554 126 558 140 588 140H964Q1000 140 1000 188V250H0V20Q0 0 20 0Z"
              />
              <path
                v-else
                d="M980 0H498C480 0 478 14 468 48L456 92C446 126 442 140 412 140H36Q0 140 0 188V250H1000V20Q1000 0 980 0Z"
              />
            </svg>
            <button
              v-for="item in primaryTabs"
              :key="item.value"
              type="button"
              class="auth-primary-tabs__item"
              :class="{ active: primaryMode === item.value }"
              :aria-selected="primaryMode === item.value"
              @click="switchPrimaryMode(item.value)"
            >
              {{ item.label }}
            </button>
          </div>
        </header>

        <div class="auth-account-scroll">
          <div v-if="primaryMode === 'login'" class="auth-login-view">
            <div class="auth-login-switcher">
              <div class="auth-login-title">
                <h3>{{ loginModeTitle }}</h3>
              </div>
            </div>

            <form
              v-if="loginMode === 'sms' || loginMode === 'email'"
              class="auth-form"
              @submit.prevent="submitSmsLogin"
            >
              <!-- 邮箱 -->
              <!-- 手机号 -->
              <label class="auth-field-label">{{
                loginMode === "email"
                  ? $t("auth.emailAddress")
                  : $t("auth.mobile")
              }}</label>
              <div v-if="loginMode === 'email'" class="auth-field">
                <!-- 请输入邮箱 -->
                <input
                  v-model.trim="loginForm.email"
                  type="email"
                  autocomplete="email"
                  :placeholder="$t('auth.enterEmailAddress')"
                />
              </div>
              <div v-else class="auth-field auth-phone-field">
                <div class="auth-area-select auth-compact-area-select">
                  <span class="auth-compact-area-value">
                    {{ loginForm.areaCode }}
                  </span>
                  <!-- 国家或地区区号 -->
                  <select
                    v-model="loginForm.areaCode"
                    :aria-label="$t('auth.areaCode')"
                  >
                    <!-- 中国大陆 +86 -->
                    <option value="+86">{{ $t("auth.areaMainland") }}</option>
                    <!-- 中国香港 +852 -->
                    <option value="+852">{{ $t("auth.areaHongKong") }}</option>
                    <!-- 中国澳门 +853 -->
                    <option value="+853">{{ $t("auth.areaMacau") }}</option>
                    <!-- 中国台湾 +886 -->
                    <option value="+886">{{ $t("auth.areaTaiwan") }}</option>
                    <!-- 美国/加拿大 +1 -->
                    <option value="+1">
                      {{ $t("auth.areaNorthAmerica") }}
                    </option>
                    <!-- 新加坡 +65 -->
                    <option value="+65">{{ $t("auth.areaSingapore") }}</option>
                  </select>
                </div>
                <!-- 请输入手机号 -->
                <input
                  v-model.trim="loginForm.mobile"
                  type="tel"
                  inputmode="numeric"
                  autocomplete="tel"
                  :placeholder="$t('auth.enterMobile')"
                />
              </div>

              <!-- 邮箱验证码 -->
              <!-- 短信验证码 -->
              <label class="auth-field-label">{{
                loginMode === "email"
                  ? $t("auth.emailCode")
                  : $t("auth.smsCode")
              }}</label>
              <div class="auth-field auth-code-field">
                <!-- 请输入验证码 -->
                <input
                  v-model.trim="loginForm.code"
                  type="text"
                  inputmode="numeric"
                  maxlength="4"
                  autocomplete="one-time-code"
                  :placeholder="$t('auth.enterCode')"
                  @input="handleLoginCodeInput"
                />
                <!-- {seconds}s 后重发 -->
                <!-- 获取验证码 -->
                <button
                  type="button"
                  :disabled="countdown.login > 0"
                  @click="sendLoginCode"
                >
                  {{
                    countdown.login > 0
                      ? $t("auth.resendAfter", { seconds: countdown.login })
                      : $t("auth.getCode")
                  }}
                </button>
              </div>
              <!-- 登录中… -->
              <!-- 登录 -->
              <button
                class="auth-primary-button"
                type="submit"
                :disabled="submitting"
              >
                {{ submitting ? $t("auth.loggingIn") : $t("auth.login") }}
              </button>
              <!-- 还没有账号？ -->
              <!-- 立即注册 -->
              <p class="auth-form-switch auth-sms-register-link">
                {{ $t("auth.noAccount") }}
                <button type="button" @click="switchPrimaryMode('register')">
                  {{ $t("auth.registerNow") }}
                </button>
              </p>
              <div class="auth-alt-login">
                <div class="auth-login-icon-actions">
                  <!-- 密码登录 -->
                  <!-- 切换到密码登录 -->
                  <!-- 验证码登录 -->
                  <!-- 切换到验证码登录 -->
                  <!-- 邮箱登录 -->
                  <!-- 短信登录 -->
                  <!-- 邮箱验证码登录 -->
                  <button
                    class="auth-code-action"
                    type="button"
                    :title="
                      loginMode === 'sms'
                        ? $t('auth.emailLogin')
                        : $t('auth.smsLogin')
                    "
                    :aria-label="
                      loginMode === 'sms'
                        ? $t('auth.emailCodeLogin')
                        : $t('auth.switchCodeLogin')
                    "
                    @click="
                      switchLoginMode(loginMode === 'sms' ? 'email' : 'sms')
                    "
                  >
                    <svg
                      v-if="loginMode === 'sms'"
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                    >
                      <rect x="3" y="5" width="18" height="14" rx="2"></rect>
                      <path d="m3 6 9 7 9-7"></path>
                    </svg>
                    <svg v-else viewBox="0 0 24 24" aria-hidden="true">
                      <rect
                        x="7"
                        y="2.5"
                        width="10"
                        height="19"
                        rx="2.4"
                      ></rect>
                      <path d="M10 5.5h4M11 18.5h2"></path>
                    </svg>
                    <!-- 邮箱登录 -->
                    <!-- 短信登录 -->
                    <span>{{
                      loginMode === "sms"
                        ? $t("auth.emailLogin")
                        : $t("auth.smsLogin")
                    }}</span>
                  </button>
                  <!-- 密码登录 -->
                  <!-- 切换到密码登录 -->
                  <button
                    class="auth-password-action"
                    type="button"
                    :title="$t('auth.passwordLogin')"
                    :aria-label="$t('auth.switchPasswordLogin')"
                    @click="switchLoginMode('password')"
                  >
                    <svg viewBox="0 0 24 24" aria-hidden="true">
                      <rect x="5" y="10" width="14" height="10" rx="2.5"></rect>
                      <path d="M8 10V7.5a4 4 0 0 1 8 0V10"></path>
                      <circle cx="12" cy="15" r="1.2"></circle>
                      <path d="M12 16.2v1.4"></path>
                    </svg>
                    <!-- 密码登录 -->
                    <span>{{ $t("auth.passwordLogin") }}</span>
                  </button>
                  <!-- 微信登录 -->
                  <!-- 切换到微信登录 -->
                  <button
                    class="auth-wechat-action"
                    type="button"
                    :title="$t('auth.wechatLogin')"
                    :aria-label="$t('auth.switchWechatLogin')"
                    @click="switchLoginMode('wechat')"
                  >
                    <svg viewBox="0 0 24 24" aria-hidden="true">
                      <path
                        d="M14.8 11.4c0-3.1-3-5.6-6.7-5.6S1.5 8.3 1.5 11.4c0 1.8 1 3.4 2.7 4.4l-.6 2.1 2.4-1.2c.7.2 1.4.3 2.1.3.4 0 .8 0 1.2-.1"
                      ></path>
                      <path
                        d="M22.5 15.2c0-2.5-2.4-4.5-5.4-4.5s-5.4 2-5.4 4.5 2.4 4.5 5.4 4.5c.6 0 1.2-.1 1.7-.2l2 1-.5-1.7c1.3-.8 2.2-2.1 2.2-3.6Z"
                      ></path>
                      <circle
                        cx="6.2"
                        cy="10.2"
                        r=".7"
                        class="auth-icon-fill"
                      ></circle>
                      <circle
                        cx="10.3"
                        cy="10.2"
                        r=".7"
                        class="auth-icon-fill"
                      ></circle>
                      <circle
                        cx="15.5"
                        cy="14.4"
                        r=".6"
                        class="auth-icon-fill"
                      ></circle>
                      <circle
                        cx="18.7"
                        cy="14.4"
                        r=".6"
                        class="auth-icon-fill"
                      ></circle>
                    </svg>
                    <!-- 微信登录 -->
                    <span>{{ $t("auth.wechatLogin") }}</span>
                  </button>
                  <!-- Google 登录 -->
                  <GoogleLoginButton
                    variant="action"
                    :label="$t('devUpdates.oct23GoogleSignIn')"
                    :agreed="agreed"
                    :disabled="submitting"
                    :referral-params="getReferralParams"
                    @agreement-required="
                      $message.warning('请先阅读并同意用户协议与隐私政策')
                    "
                    @busy="submitting = $event"
                    @success="handleGoogleLogin"
                  />
                </div>
              </div>
            </form>

            <form
              v-else-if="loginMode === 'password'"
              class="auth-form"
              @submit.prevent="submitPasswordLogin"
            >
              <!-- 手机号 / 邮箱 -->
              <label class="auth-field-label">{{
                $t("auth.mobileOrEmail")
              }}</label>
              <div class="auth-field">
                <!-- 请输入手机号或邮箱 -->
                <input
                  v-model.trim="loginForm.account"
                  type="text"
                  autocomplete="username"
                  :placeholder="$t('auth.enterMobileOrEmail')"
                />
              </div>

              <div class="auth-label-row">
                <!-- 密码 -->
                <label class="auth-field-label">{{
                  $t("auth.password")
                }}</label>
                <!-- 忘记密码 -->
                <button
                  type="button"
                  class="auth-text-button"
                  @click="showForgotTip"
                >
                  {{ $t("auth.forgotPassword") }}
                </button>
              </div>
              <div class="auth-field auth-password-field">
                <input
                  v-model="loginForm.password"
                  :type="showLoginPassword ? 'text' : 'password'"
                  autocomplete="current-password"
                  :placeholder="$t('auth.enterLoginPassword')"
                />
                <!-- 隐藏密码 -->
                <!-- 显示密码 -->
                <button
                  type="button"
                  :aria-label="
                    showLoginPassword
                      ? $t('auth.hidePassword')
                      : $t('auth.showPassword')
                  "
                  :title="
                    showLoginPassword
                      ? $t('auth.hidePassword')
                      : $t('auth.showPassword')
                  "
                  @click="showLoginPassword = !showLoginPassword"
                >
                  <svg
                    v-if="showLoginPassword"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path d="M3 3l18 18" />
                    <path d="M10.6 10.6a2 2 0 0 0 2.8 2.8" />
                    <path
                      d="M9.9 4.2A9.8 9.8 0 0 1 12 4c6 0 9.5 8 9.5 8a17.7 17.7 0 0 1-2.1 3.2"
                    />
                    <path
                      d="M6.6 6.6C3.8 8.5 2.5 12 2.5 12s3.5 8 9.5 8a9.8 9.8 0 0 0 5.4-1.6"
                    />
                  </svg>
                  <svg v-else viewBox="0 0 24 24" aria-hidden="true">
                    <path
                      d="M2.5 12s3.5-6 9.5-6 9.5 6 9.5 6-3.5 6-9.5 6-9.5-6-9.5-6Z"
                    />
                    <circle cx="12" cy="12" r="2.5" />
                  </svg>
                </button>
              </div>

              <!-- 登录 -->
              <button class="auth-primary-button" type="submit">
                {{ $t("auth.login") }}
              </button>
              <!-- 还没有账号？ -->
              <!-- 立即注册 -->
              <p class="auth-form-switch auth-sms-register-link">
                {{ $t("auth.noAccount") }}
                <button type="button" @click="switchPrimaryMode('register')">
                  {{ $t("auth.registerNow") }}
                </button>
              </p>
              <div class="auth-alt-login">
                <div class="auth-login-icon-actions">
                  <!-- 短信登录 -->
                  <!-- 切换到短信登录 -->
                  <!-- 邮箱登录 -->
                  <button
                    class="auth-email-action"
                    type="button"
                    :title="$t('auth.emailLogin')"
                    :aria-label="$t('auth.emailLogin')"
                    @click="switchLoginMode('email')"
                  >
                    <svg viewBox="0 0 24 24" aria-hidden="true">
                      <rect x="3" y="5" width="18" height="14" rx="2"></rect>
                      <path d="m3 6 9 7 9-7"></path>
                    </svg>
                    <!-- 邮箱登录 -->
                    <span>{{ $t("auth.emailLogin") }}</span>
                  </button>
                  <!-- 验证码登录 -->
                  <!-- 切换到验证码登录 -->
                  <button
                    class="auth-sms-action"
                    type="button"
                    :title="$t('auth.codeLogin')"
                    :aria-label="$t('auth.switchCodeLogin')"
                    @click="switchLoginMode('sms')"
                  >
                    <svg viewBox="0 0 24 24" aria-hidden="true">
                      <path d="M4 5.5h16v11H9l-4.5 3v-3H4z"></path>
                      <path d="M8 10.8h.01M12 10.8h.01M16 10.8h.01"></path>
                    </svg>
                    <!-- 验证码登录 -->
                    <span>{{ $t("auth.codeLogin") }}</span>
                  </button>
                  <!-- 微信登录 -->
                  <!-- 切换到微信登录 -->
                  <button
                    class="auth-wechat-action"
                    type="button"
                    :title="$t('auth.wechatLogin')"
                    :aria-label="$t('auth.switchWechatLogin')"
                    @click="switchLoginMode('wechat')"
                  >
                    <svg viewBox="0 0 24 24" aria-hidden="true">
                      <path
                        d="M14.8 11.4c0-3.1-3-5.6-6.7-5.6S1.5 8.3 1.5 11.4c0 1.8 1 3.4 2.7 4.4l-.6 2.1 2.4-1.2c.7.2 1.4.3 2.1.3.4 0 .8 0 1.2-.1"
                      ></path>
                      <path
                        d="M22.5 15.2c0-2.5-2.4-4.5-5.4-4.5s-5.4 2-5.4 4.5 2.4 4.5 5.4 4.5c.6 0 1.2-.1 1.7-.2l2 1-.5-1.7c1.3-.8 2.2-2.1 2.2-3.6Z"
                      ></path>
                      <circle
                        cx="6.2"
                        cy="10.2"
                        r=".7"
                        class="auth-icon-fill"
                      ></circle>
                      <circle
                        cx="10.3"
                        cy="10.2"
                        r=".7"
                        class="auth-icon-fill"
                      ></circle>
                      <circle
                        cx="15.5"
                        cy="14.4"
                        r=".6"
                        class="auth-icon-fill"
                      ></circle>
                      <circle
                        cx="18.7"
                        cy="14.4"
                        r=".6"
                        class="auth-icon-fill"
                      ></circle>
                    </svg>
                    <!-- 微信登录 -->
                    <span>{{ $t("auth.wechatLogin") }}</span>
                  </button>
                  <!-- Google 登录 -->
                  <GoogleLoginButton
                    variant="action"
                    :label="$t('devUpdates.oct23GoogleSignIn')"
                    :agreed="agreed"
                    :disabled="submitting"
                    :referral-params="getReferralParams"
                    @agreement-required="
                      $message.warning('请先阅读并同意用户协议与隐私政策')
                    "
                    @busy="submitting = $event"
                    @success="handleGoogleLogin"
                  />
                </div>
              </div>
            </form>

            <div v-else class="auth-wechat-view">
              <div class="auth-qr-frame" :class="{ invalid: qrInvalid }">
                <!-- 微信登录二维码 -->
                <img
                  v-if="wxLoginUrl"
                  :src="wxLoginUrl"
                  :alt="$t('auth.wechatLoginQr')"
                />
                <div v-else class="auth-qr-loading">
                  <span></span>
                  <!-- 二维码加载中 -->
                  <p>{{ $t("auth.qrLoading") }}</p>
                </div>
                <div v-if="qrInvalid" class="auth-qr-invalid">
                  <!-- 二维码已失效 -->
                  <p>{{ $t("auth.qrExpired") }}</p>
                  <!-- 点击刷新 -->
                  <button type="button" @click="refreshQrcode">
                    {{ $t("auth.refreshQr") }}
                  </button>
                </div>
              </div>
              <!-- 使用微信扫码登录 -->
              <h3>{{ $t("auth.scanWechatToLogin") }}</h3>
              <!-- 打开微信扫一扫，确认后即可登录 -->
              <p>{{ $t("auth.scanWechatLoginHint") }}</p>
              <!-- 返回短信验证码登录 -->
              <!-- 返回验证码登录 -->
              <button
                class="auth-return-sms"
                type="button"
                @click="switchLoginMode('sms')"
              >
                {{ $t("auth.returnCodeLogin") }}
              </button>
            </div>
          </div>

          <div v-else class="auth-register-view">
            <form
              v-if="registerStep === 1"
              class="auth-register-step auth-form"
              @submit.prevent="validateRegisterAccount"
            >
              <div class="auth-register-type-section">
                <!-- 账号类型 -->
                <div
                  class="auth-register-account-control"
                  role="group"
                  :aria-label="$t('auth.accountType')"
                >
                  <button
                    type="button"
                    :class="{ active: registerAccountType === 'personal' }"
                    :aria-pressed="registerAccountType === 'personal'"
                    @click="switchRegisterAccountType('personal')"
                  >
                    <svg
                      class="auth-register-account-icon"
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                    >
                      <circle cx="12" cy="8" r="3.5" />
                      <path d="M5 21v-2a7 7 0 0 1 14 0v2" />
                    </svg>
                    <span class="auth-register-account-copy">
                      <!-- 个人账号 -->
                      <b>{{ $t("auth.personalAccount") }}</b>
                    </span>
                  </button>
                  <!-- 企业账号 -->
                  <button
                    type="button"
                    :class="{ active: registerAccountType === 'company' }"
                    :aria-pressed="registerAccountType === 'company'"
                    @click="switchRegisterAccountType('company')"
                  >
                    <svg
                      class="auth-register-account-icon"
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                    >
                      <path
                        d="M4 21V3h12v18M16 9h4v12M2 21h20M8 7h4M8 11h4M8 15h4M9 21v-3h2v3"
                      />
                    </svg>
                    <span class="auth-register-account-copy">
                      <!-- 企业账号 -->
                      <b>{{ $t("auth.companyAccount") }}</b>
                    </span>
                  </button>
                </div>
              </div>

              <div class="auth-register-region-section">
                <div class="auth-register-region-select">
                  <!-- 注册地区 -->
                  <label for="xsou-register-region">{{
                    $t("mobileLogin.registerRegion")
                  }}</label>
                  <el-select
                    id="xsou-register-region"
                    :value="registerRegion"
                    class="auth-register-region-select-control"
                    popper-class="xsou-auth-region-dropdown"
                    :popper-append-to-body="true"
                    @change="switchRegisterRegion"
                  >
                    <!-- 中国大陆 -->
                    <el-option :label="$t('auth.mainland')" value="mainland">
                      <!-- 中国大陆 -->
                      <span>{{ $t("auth.mainland") }}</span>
                    </el-option>
                    <!-- 港澳台及海外 -->
                    <el-option :label="$t('auth.overseas')" value="overseas">
                      <!-- 港澳台及海外 -->
                      <span>{{ $t("auth.overseas") }}</span>
                    </el-option>
                  </el-select>
                </div>
              </div>

              <div
                v-if="isWechatRegistration"
                class="auth-register-wechat-view"
              >
                <div class="auth-qr-frame" :class="{ invalid: qrInvalid }">
                  <!-- 微信注册二维码 -->
                  <img
                    v-if="wxLoginUrl"
                    :src="wxLoginUrl"
                    :alt="$t('auth.wechatRegisterQr')"
                  />
                  <div v-else class="auth-qr-loading">
                    <span></span>
                    <!-- 二维码加载中 -->
                    <p>{{ $t("auth.qrLoading") }}</p>
                  </div>
                  <div v-if="qrInvalid" class="auth-qr-invalid">
                    <!-- 二维码已失效 -->
                    <p>{{ $t("auth.qrExpired") }}</p>
                    <!-- 点击刷新 -->
                    <button type="button" @click="refreshQrcode">
                      {{ $t("auth.refreshQr") }}
                    </button>
                  </div>
                </div>
                <!-- 使用微信扫码注册 -->
                <h3>{{ $t("auth.scanWechatToRegister") }}</h3>
                <!-- 打开微信扫一扫，确认后即可完成注册并登录 -->
                <p>{{ $t("auth.scanWechatRegisterHint") }}</p>
                <!-- 返回手机号注册 -->
                <!-- 返回邮箱注册 -->
                <button
                  class="auth-return-register-default"
                  type="button"
                  @click="switchRegisterMethod(defaultRegisterChannel)"
                >
                  {{
                    defaultRegisterChannel === "mobile"
                      ? $t("auth.returnMobileRegistration")
                      : $t("auth.returnEmailRegistration")
                  }}
                </button>
              </div>

              <template v-else>
                <div class="auth-register-verification-fields">
                  <div>
                    <!-- 中国大陆手机号 -->
                    <!-- 港澳台及海外手机号 -->
                    <!-- 常用邮箱 -->
                    <label class="auth-field-label">{{
                      registerChannel === "mobile"
                        ? registerRegion === "mainland"
                          ? $t("auth.mainlandMobile")
                          : $t("auth.overseasMobile")
                        : $t("auth.email")
                    }}</label>
                    <div
                      v-if="registerChannel === 'mobile'"
                      class="auth-field auth-phone-field"
                    >
                      <span
                        v-if="registerRegion === 'mainland'"
                        class="auth-fixed-area-code"
                      >
                        +86
                      </span>
                      <div
                        v-else
                        class="auth-area-select auth-compact-area-select"
                      >
                        <span class="auth-compact-area-value">
                          {{ registerForm.countryCode }}
                        </span>
                        <!-- 注册手机号国家或地区区号 -->
                        <select
                          v-model="registerForm.countryCode"
                          :aria-label="$t('auth.registerAreaCode')"
                        >
                          <option
                            v-for="option in overseasAreaCodeOptions"
                            :key="option.value"
                            :value="option.value"
                          >
                            {{ option.label }}
                          </option>
                        </select>
                      </div>
                      <!-- 请输入手机号 -->
                      <input
                        v-model.trim="registerForm.mobile"
                        type="tel"
                        autocomplete="tel"
                        :placeholder="$t('auth.enterMobile')"
                      />
                    </div>
                    <div v-else class="auth-field">
                      <!-- 请输入常用邮箱 -->
                      <input
                        v-model.trim="registerForm.email"
                        type="email"
                        autocomplete="email"
                        :placeholder="$t('auth.enterEmail')"
                      />
                    </div>
                  </div>

                  <div>
                    <!-- 验证码 -->
                    <label class="auth-field-label">{{
                      $t("auth.code")
                    }}</label>
                    <div class="auth-field auth-code-field">
                      <!-- 请输入4位验证码 -->
                      <input
                        v-model.trim="registerForm.code"
                        inputmode="numeric"
                        maxlength="4"
                        autocomplete="one-time-code"
                        :placeholder="$t('auth.enterFourDigitCode')"
                      />
                      <!-- {seconds}s 后重发 -->
                      <!-- 获取验证码 -->
                      <button
                        type="button"
                        :disabled="countdown.register > 0"
                        @click="sendRegisterCode"
                      >
                        {{
                          countdown.register > 0
                            ? $t("auth.resendAfter", {
                                seconds: countdown.register,
                              })
                            : $t("auth.getCode")
                        }}
                      </button>
                    </div>
                  </div>
                </div>

                <div class="auth-register-password-block">
                  <div>
                    <!-- 登录密码 -->
                    <label class="auth-field-label">{{
                      $t("auth.loginPassword")
                    }}</label>
                    <div class="auth-field auth-password-field">
                      <input
                        v-model="registerForm.password"
                        :type="showRegisterPassword ? 'text' : 'password'"
                        autocomplete="new-password"
                        :placeholder="$t('auth.enterPasswordLength')"
                      />
                      <!-- 隐藏密码 -->
                      <!-- 显示密码 -->
                      <button
                        type="button"
                        :aria-label="
                          showRegisterPassword
                            ? $t('auth.hidePassword')
                            : $t('auth.showPassword')
                        "
                        :title="
                          showRegisterPassword
                            ? $t('auth.hidePassword')
                            : $t('auth.showPassword')
                        "
                        @click="showRegisterPassword = !showRegisterPassword"
                      >
                        <svg
                          v-if="showRegisterPassword"
                          viewBox="0 0 24 24"
                          aria-hidden="true"
                        >
                          <path d="M3 3l18 18" />
                          <path d="M10.6 10.6a2 2 0 0 0 2.8 2.8" />
                          <path
                            d="M9.9 4.2A9.8 9.8 0 0 1 12 4c6 0 9.5 8 9.5 8a17.7 17.7 0 0 1-2.1 3.2"
                          />
                          <path
                            d="M6.6 6.6C3.8 8.5 2.5 12 2.5 12s3.5 8 9.5 8a9.8 9.8 0 0 0 5.4-1.6"
                          />
                        </svg>
                        <svg v-else viewBox="0 0 24 24" aria-hidden="true">
                          <path
                            d="M2.5 12s3.5-6 9.5-6 9.5 6 9.5 6-3.5 6-9.5 6-9.5-6-9.5-6Z"
                          />
                          <circle cx="12" cy="12" r="2.5" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>

                <div
                  v-if="registerAccountType === 'company'"
                  class="auth-company-fields"
                >
                  <div>
                    <!-- 企业名称 -->
                    <label class="auth-field-label">{{
                      $t("auth.companyName")
                    }}</label>
                    <div class="auth-field">
                      <!-- 请输入企业全称 -->
                      <input
                        v-model.trim="registerForm.companyName"
                        type="text"
                        autocomplete="organization"
                        :placeholder="$t('auth.enterCompanyName')"
                      />
                    </div>
                  </div>

                  <div>
                    <!-- 联系人姓名 -->
                    <label class="auth-field-label">{{
                      $t("auth.contactName")
                    }}</label>
                    <div class="auth-field">
                      <!-- 请输入联系人姓名 -->
                      <input
                        v-model.trim="registerForm.contactName"
                        type="text"
                        autocomplete="name"
                        :placeholder="$t('auth.enterContactName')"
                      />
                    </div>
                  </div>
                </div>

                <!-- 注册中… -->
                <!-- 完成注册 -->
                <button
                  class="auth-primary-button"
                  type="submit"
                  :disabled="submitting"
                >
                  {{
                    submitting
                      ? $t("auth.registering")
                      : $t("auth.finishRegistration")
                  }}
                </button>
                <!-- 已有账号？ -->
                <!-- 立即登录 -->
                <p class="auth-register-login-link">
                  {{ $t("auth.haveAccount") }}
                  <button type="button" @click="switchPrimaryMode('login')">
                    {{ $t("auth.loginNow") }}
                  </button>
                </p>
                <div class="auth-register-method-actions">
                  <button
                    type="button"
                    :title="alternateRegisterMethodLabel"
                    @click="switchRegisterMethod(alternateRegisterMethod)"
                  >
                    <svg
                      v-if="alternateRegisterMethod === 'email'"
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                    >
                      <rect x="3" y="5" width="18" height="14" rx="2.5"></rect>
                      <path d="m4.5 7 7.5 6 7.5-6"></path>
                    </svg>
                    <svg v-else viewBox="0 0 24 24" aria-hidden="true">
                      <rect
                        x="7"
                        y="2.5"
                        width="10"
                        height="19"
                        rx="2.4"
                      ></rect>
                      <path d="M10 5.5h4M11 18.5h2"></path>
                    </svg>
                    <span>{{ alternateRegisterMethodLabel }}</span>
                  </button>
                  <!-- 微信注册 -->
                  <button
                    v-if="registerAccountType === 'personal'"
                    class="auth-register-wechat-action"
                    type="button"
                    :title="$t('auth.wechatRegistration')"
                    @click="switchRegisterMethod('wechat')"
                  >
                    <svg viewBox="0 0 24 24" aria-hidden="true">
                      <path
                        d="M14.8 11.4c0-3.1-3-5.6-6.7-5.6S1.5 8.3 1.5 11.4c0 1.8 1 3.4 2.7 4.4l-.6 2.1 2.4-1.2c.7.2 1.4.3 2.1.3.4 0 .8 0 1.2-.1"
                      ></path>
                      <path
                        d="M22.5 15.2c0-2.5-2.4-4.5-5.4-4.5s-5.4 2-5.4 4.5 2.4 4.5 5.4 4.5c.6 0 1.2-.1 1.7-.2l2 1-.5-1.7c1.3-.8 2.2-2.1 2.2-3.6Z"
                      ></path>
                      <circle
                        cx="6.2"
                        cy="10.2"
                        r=".7"
                        class="auth-icon-fill"
                      ></circle>
                      <circle
                        cx="10.3"
                        cy="10.2"
                        r=".7"
                        class="auth-icon-fill"
                      ></circle>
                      <circle
                        cx="15.5"
                        cy="14.4"
                        r=".6"
                        class="auth-icon-fill"
                      ></circle>
                      <circle
                        cx="18.7"
                        cy="14.4"
                        r=".6"
                        class="auth-icon-fill"
                      ></circle>
                    </svg>
                    <!-- 微信注册 -->
                    <span>{{ $t("auth.wechatRegistration") }}</span>
                  </button>
                  <!-- Google 注册 -->
                  <GoogleLoginButton
                    variant="action"
                    v-if="registerAccountType === 'personal'"
                    :label="$t('devUpdates.oct23GoogleSignUp')"
                    :agreed="agreed"
                    :disabled="submitting"
                    :referral-params="getReferralParams"
                    @agreement-required="
                      $message.warning('请先阅读并同意用户协议与隐私政策')
                    "
                    @busy="submitting = $event"
                    @success="handleGoogleLogin"
                  />
                </div>
              </template>
              <!-- 已有账号？ -->
              <!-- 立即登录 -->
              <p v-if="isWechatRegistration" class="auth-register-login-link">
                {{ $t("auth.haveAccount") }}
                <button type="button" @click="switchPrimaryMode('login')">
                  {{ $t("auth.loginNow") }}
                </button>
              </p>
            </form>

            <div v-else class="auth-register-success">
              <div class="auth-success-mark">✓</div>
              <!-- 注册成功 -->
              <span>{{ $t("auth.registrationSuccess") }}</span>
              <!-- 企业账号已创建 -->
              <!-- 个人账号已创建 -->
              <h3>
                {{
                  registerAccountType === "company"
                    ? $t("auth.companyAccountCreated")
                    : $t("auth.personalAccountCreated")
                }}
              </h3>
              <p>{{ registerSuccessDescription }}</p>

              <template v-if="registerAccountType === 'company'">
                <!-- 继续企业认证 -->
                <button
                  class="auth-primary-button"
                  type="button"
                  @click="finishRegistration(true)"
                >
                  {{ $t("auth.continueCompanyCertification") }}
                </button>
                <!-- 暂时跳过，先进入 Xsou -->
                <button
                  class="auth-later-button auth-success-later"
                  type="button"
                  @click="finishRegistration(false)"
                >
                  {{ $t("auth.skipAndEnter") }}
                </button>
              </template>
              <!-- 进入 Xsou -->
              <button
                v-else
                class="auth-primary-button"
                type="button"
                @click="finishRegistration(false)"
              >
                {{ $t("auth.enterXsou") }}
              </button>
            </div>
          </div>
        </div>

        <footer
          v-if="primaryMode === 'login' || registerStep < 3"
          class="auth-account-footer"
        >
          <label class="auth-agreement-check">
            <input v-model="agreed" type="checkbox" />
            <span></span>
          </label>
          <!-- 我已阅读并同意 -->
          <!-- 《用户协议》 -->
          <!-- 和 -->
          <!-- 《隐私政策》 -->
          <p>
            {{ $t("auth.agreementPrefix") }}
            <a href="javascript:;">{{ $t("auth.userAgreement") }}</a>
            {{ $t("auth.agreementConjunction") }}
            <a href="javascript:;">{{ $t("auth.privacyPolicy") }}</a>
          </p>
        </footer>
      </section>
    </div>
    <PasswordResetDialog v-model="resetVisible" @success="loginForm.password = ''" :initial-account="loginForm.account || ''" :initial-country-code="loginForm.areaCode || '+86'" />
  </el-dialog>
</template>

<script>
import PasswordResetDialog from "@/components/PasswordResetDialog.vue";
import devSyncI18n from "@/i18n/common/index";

import {
  getMsgCode,
  sendEmailCode,
  codeLogin,
  emailCodeLogin,
  passwordLogin,
  registerUser,
  getWxmpCode,
  selectWxmpLoginStatus,
} from "@/api/login";
import { getUserInfoDetail } from "@/api/user.js";
import { encryptPassword } from "@/utils/passwordEncrypt";
import { resolveHomeDoodle } from "@/indexlogo/schedule";
import GoogleLoginButton from "@/components/GoogleLoginButton.vue";

export default {
  mixins: [devSyncI18n],
  name: "AuthDialog",
  components: { GoogleLoginButton, PasswordResetDialog },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    initialMode: {
      type: String,
      default: "login",
      validator: (value) => ["login", "register"].includes(value),
    },
    initialLoginMode: {
      type: String,
      default: "sms",
      validator: (value) =>
        ["sms", "email", "password", "wechat", "register"].includes(value),
    },
  },
  data() {
    return {
      resetVisible: false,
      primaryMode: "login",
      loginMode: "sms",
      registerStep: 1,
      registerRegion: "mainland",
      registerAccountType: "personal",
      registerMethod: "mobile",
      wechatPurpose: "login",
      agreed: false,
      submitting: false,
      showLoginPassword: false,
      showRegisterPassword: false,
      loginForm: {
        areaCode: "+86",
        mobile: "",
        email: "",
        code: "",
        account: "",
        password: "",
      },
      registerForm: {
        countryCode: "+86",
        mobile: "",
        email: "",
        code: "",
        password: "",
        displayName: "",
        companyName: "",
        contactName: "",
      },
      registeredUser: null,
      wxLoginUrl: "",
      wxTicket: "",
      wxUuid: "",
      qrInvalid: false,
      qrStartedAt: 0,
      countdown: { login: 0, register: 0 },
      timers: { login: null, register: null, wechat: null },
      activeDoodle: null,
      doodleTimer: null,
    };
  },
  computed: {
    primaryTabs() {
      return [
        // 登录
        { label: this.$t("auth.login"), value: "login" },
        // 注册
        { label: this.$t("auth.register"), value: "register" },
      ];
    },
    authLogoSrc() {
      return (
        this.activeDoodle?.navImage || require("@/assets/bg/xsou logo.png")
      );
    },
    authLogoAlt() {
      return this.activeDoodle
        ? `先搜${this.activeDoodle.name}登录标志`
        : "Xsou";
    },
    festivalThemeClass() {
      const theme = this.activeDoodle?.themeKey || this.activeDoodle?.key;
      return theme ? `auth-festival-${theme}` : "";
    },
    dialogVisible: {
      get() {
        return this.visible;
      },
      set(value) {
        this.$emit("update:visible", value);
      },
    },
    loginModeTitle() {
      return {
        // 短信登录
        sms: this.$t("auth.smsLogin"),
        // 邮箱验证码登录
        email: this.$t("auth.emailCodeLogin"),
        // 密码登录
        password: this.$t("auth.passwordLogin"),
        // 微信登录
        wechat: this.$t("auth.wechatLogin"),
      }[this.loginMode];
    },
    defaultRegisterChannel() {
      return this.registerRegion === "mainland" ? "mobile" : "email";
    },
    registerChannel() {
      if (this.registerMethod !== "wechat") {
        return this.registerMethod;
      }
      return this.defaultRegisterChannel;
    },
    isWechatRegistration() {
      return (
        this.registerAccountType === "personal" &&
        this.registerMethod === "wechat"
      );
    },
    alternateRegisterMethod() {
      return this.registerChannel === "mobile" ? "email" : "mobile";
    },
    alternateRegisterMethodLabel() {
      return this.alternateRegisterMethod === "email"
        ? // 邮箱注册
          // 邮箱注册
          this.$t("auth.emailRegistration")
        : // 手机号注册
          // 手机号注册
          this.$t("auth.mobileRegistration");
    },
    registerRegionLabel() {
      return this.registerRegion === "mainland"
        ? // 中国大陆
          // 中国大陆
          this.$t("auth.mainland")
        : // 港澳台及海外
          // 港澳台及海外
          this.$t("auth.overseas");
    },
    registerAccountTypeLabel() {
      return this.registerAccountType === "company"
        ? // 企业账号
          // 企业账号
          this.$t("auth.companyAccount")
        : // 个人账号
          // 个人账号
          this.$t("auth.personalAccount");
    },
    registerContextLabel() {
      return `${this.registerRegionLabel} · ${this.registerAccountTypeLabel}`;
    },
    registerSuccessDescription() {
      // {context}注册已完成。
      const completion = this.$t("auth.registrationCompleted", {
        context: this.registerContextLabel,
      });
      const hint =
        this.registerAccountType === "company"
          ? // 继续认证后可使用完整企业功能。
            // 继续认证后可使用完整企业功能。
            this.$t("auth.companyCertificationHint")
          : // 现在可以直接进入 Xsou。
            // 现在可以直接进入 Xsou。
            this.$t("auth.enterXsouHint");
      return `${completion}${this.$t("auth.sentenceSeparator")}${hint}`;
    },
    registerAccountDisplay() {
      return this.registerChannel === "mobile"
        ? `${this.registerForm.countryCode} ${this.registerForm.mobile}`
        : this.registerForm.email;
    },
    overseasAreaCodeOptions() {
      return [
        // 中国香港 +852
        { label: this.$t("auth.areaHongKong"), value: "+852" },
        // 中国澳门 +853
        { label: this.$t("auth.areaMacau"), value: "+853" },
        // 中国台湾 +886
        { label: this.$t("auth.areaTaiwan"), value: "+886" },
        // 美国/加拿大 +1
        { label: this.$t("auth.areaNorthAmerica"), value: "+1" },
        // 新加坡 +65
        { label: this.$t("auth.areaSingapore"), value: "+65" },
      ];
    },
  },
  watch: {
    visible(value) {
      if (value) {
        this.primaryMode = this.initialMode;
        if (this.primaryMode === "login") {
          this.loginMode =
            this.initialLoginMode === "register"
              ? "sms"
              : this.initialLoginMode;
        }
        if (this.primaryMode === "register") {
          this.registerStep = 1;
          this.registerMethod = this.defaultRegisterChannel;
        }
        this.$nextTick(() => {
          const scrollContainer = this.$el.querySelector(
            ".auth-account-scroll",
          );
          if (scrollContainer) scrollContainer.scrollTop = 0;
        });
        if (this.primaryMode === "login" && this.loginMode === "wechat") {
          this.loadWechatQrcode();
        }
      } else {
        this.clearAllTimers();
      }
    },
    initialMode(value) {
      if (this.visible) this.primaryMode = value;
    },
  },
  created() {
    this.refreshDoodle();
    this.doodleTimer = window.setInterval(this.refreshDoodle, 10 * 1000);
  },
  beforeDestroy() {
    this.clearAllTimers();
    window.clearInterval(this.doodleTimer);
  },
  methods: {
    refreshDoodle() {
      const override =
        this.$route?.query?.doodle ||
        new URLSearchParams(window.location.search).get("doodle") ||
        "";
      this.activeDoodle = resolveHomeDoodle(new Date(), override);
    },
    closeDialog() {
      this.dialogVisible = false;
    },
    handleClosed() {
      this.clearAllTimers();
      this.qrInvalid = false;
      this.wechatPurpose = "login";
    },
    switchPrimaryMode(mode) {
      this.primaryMode = mode;
      if (mode === "login") this.loginMode = "sms";
      if (mode === "register") {
        this.registerStep = 1;
        this.registerMethod = this.defaultRegisterChannel;
        this.resetRegisterCountryCode();
        this.registeredUser = null;
      }
      this.$nextTick(() => {
        const scrollContainer = this.$el.querySelector(".auth-account-scroll");
        if (scrollContainer) scrollContainer.scrollTop = 0;
      });
      this.clearTimer("wechat");
      if (mode === "login" && this.loginMode === "wechat")
        this.loadWechatQrcode();
    },
    switchLoginMode(mode) {
      if (mode === "wechat" && !this.ensureAgreement()) return;
      if (this.loginMode !== mode) {
        this.loginForm.code = "";
        this.clearTimer("login");
        this.$set(this.countdown, "login", 0);
      }
      this.loginMode = mode;
      this.clearTimer("wechat");
      if (mode === "wechat") {
        this.wechatPurpose = "login";
        this.loadWechatQrcode();
      }
    },
    switchRegisterRegion(region) {
      if (this.registerRegion === region) return;
      this.clearTimer("wechat");
      this.registerRegion = region;
      this.registerMethod = this.defaultRegisterChannel;
      this.resetRegisterCountryCode();
      this.registerForm.code = "";
      this.clearTimer("register");
      this.$set(this.countdown, "register", 0);
    },
    switchRegisterAccountType(accountType) {
      if (this.registerAccountType === accountType) return;
      this.clearTimer("wechat");
      this.registerAccountType = accountType;
      this.registerMethod = this.defaultRegisterChannel;
      this.resetRegisterCountryCode();
      this.registerForm.code = "";
      this.clearTimer("register");
      this.$set(this.countdown, "register", 0);
    },
    switchRegisterMethod(method) {
      if (
        !["mobile", "email", "wechat"].includes(method) ||
        (method === "wechat" && this.registerAccountType !== "personal") ||
        this.registerMethod === method
      )
        return;
      const previousMethod = this.registerMethod;
      this.clearTimer("wechat");
      this.registerMethod = method;
      if (method === "mobile") this.resetRegisterCountryCode();
      this.registerForm.code = "";
      this.clearTimer("register");
      this.$set(this.countdown, "register", 0);
      this.qrInvalid = false;
      if (method === "wechat") {
        if (!this.ensureAgreement()) {
          this.registerMethod = previousMethod;
          return;
        }
        this.wechatPurpose = "register";
        this.loadWechatQrcode();
      }
      this.scrollAuthPanelToTop();
    },
    ensureAgreement() {
      if (this.agreed) return true;
      this.$message.warning(this.$t("mobileLogin.agreementRequired"));
      return false;
    },
    resetRegisterCountryCode() {
      this.registerForm.countryCode =
        this.registerRegion === "mainland" ? "+86" : "+852";
    },
    isValidMobile(value, countryCode = "+86") {
      if (countryCode === "+86") return /^1[3-9]\d{9}$/.test(value);
      if (countryCode === "+852") return /^[4-9]\d{7}$/.test(value);
      return /^\d{6,20}$/.test(value);
    },
    isValidEmail(value) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
    },
    handleLoginCodeInput(event) {
      const code = String(event.target.value || "")
        .replace(/\D/g, "")
        .slice(0, 4);
      event.target.value = code;
      this.loginForm.code = code;
    },
    startCountdown(type) {
      if (this.countdown[type] > 0) return;
      this.$set(this.countdown, type, 60);
      this.clearTimer(type);
      this.timers[type] = window.setInterval(() => {
        const next = this.countdown[type] - 1;
        this.$set(this.countdown, type, Math.max(next, 0));
        if (next <= 0) this.clearTimer(type);
      }, 1000);
    },
    async sendLoginCode() {
      const emailMode = this.loginMode === "email";
      if (
        emailMode
          ? !this.isValidEmail(this.loginForm.email)
          : !this.isValidMobile(this.loginForm.mobile, this.loginForm.areaCode)
      ) {
        // 请输入正确的邮箱
        // 请输入正确的手机号
        this.$message.warning(
          emailMode
            ? // 请输入正确的邮箱
              this.$t("auth.invalidEmail")
            : // 请输入正确的手机号
              this.$t("auth.invalidMobile"),
        );
        return;
      }
      if (!emailMode && !["+86", "+852"].includes(this.loginForm.areaCode)) {
        // 短信目前支持中国大陆和中国香港，其他地区请使用邮箱登录
        this.$message.info(this.$t("auth.supportedSmsRegions"));
        return;
      }
      try {
        const response = emailMode
          ? await sendEmailCode(this.loginForm.email, "login")
          : await getMsgCode(
              this.loginForm.mobile,
              this.loginForm.areaCode,
              "login",
            );
        // 验证码发送失败
        if (response.code !== 200)
          throw new Error(
            // 验证码发送失败
            response.msg || this.$t("auth.verificationCodeSendFailed"),
          );
        this.startCountdown("login");
        // 邮箱验证码已发送
        // 短信验证码已发送
        this.$message.success(
          emailMode
            ? // 邮箱验证码已发送
              this.$t("auth.emailVerificationCodeSent")
            : // 短信验证码已发送
              this.$t("auth.smsSent"),
        );
      } catch (error) {
        this.$message.error(this.getErrorMessage(error));
      }
    },
    async submitSmsLogin() {
      if (!this.ensureAgreement()) return;
      const emailMode = this.loginMode === "email";
      if (!emailMode && !["+86", "+852"].includes(this.loginForm.areaCode)) {
        // 短信目前支持中国大陆和中国香港，其他地区请使用邮箱登录
        this.$message.info(this.$t("auth.supportedSmsRegions"));
        return;
      }
      if (
        emailMode
          ? !this.isValidEmail(this.loginForm.email)
          : !this.isValidMobile(this.loginForm.mobile, this.loginForm.areaCode)
      ) {
        // 请输入正确的邮箱
        // 请输入正确的手机号
        this.$message.warning(
          emailMode
            ? // 请输入正确的邮箱
              this.$t("auth.invalidEmail")
            : // 请输入正确的手机号
              this.$t("auth.invalidMobile"),
        );
        return;
      }
      if (!/^\d{4}$/.test(this.loginForm.code)) {
        // 请输入4位数字验证码
        this.$message.warning(this.$t("auth.invalidFourDigitCode"));
        return;
      }
      if (this.submitting) return;
      try {
        this.submitting = true;
        const response = emailMode
          ? await emailCodeLogin(this.loginForm.email, this.loginForm.code)
          : await codeLogin(
              this.loginForm.mobile,
              this.loginForm.code,
              this.loginForm.areaCode,
            );
        if (response.code === 200) await this.applyLoginResult(response.data);
      } catch (error) {
        console.error(this.getErrorMessage(error));
      } finally {
        this.submitting = false;
      }
    },
    async submitPasswordLogin() {
      if (!this.ensureAgreement()) return;
      if (!this.loginForm.account || !this.loginForm.password) {
        // 请输入账号和密码
        this.$message.warning(this.$t("auth.accountAndPasswordRequired"));
        return;
      }
      if (this.submitting) return;
      try {
        this.submitting = true;
        const encryptedPassword = await encryptPassword(
          this.loginForm.password,
        );
        const response = await passwordLogin(
          this.loginForm.account,
          encryptedPassword,
        );
        if (response.code === 200) await this.applyLoginResult(response.data);
        // else this.$message.error(response.msg || "登录失败，请稍后重试");
      } catch (error) {
        console.error(this.getErrorMessage(error));
      } finally {
        this.submitting = false;
      }
    },
    showForgotTip() { this.resetVisible = true; },
    generateUuid() {
      return `${Date.now().toString(36)}${Math.random()
        .toString(36)
        .slice(2, 12)}`;
    },
    loadWechatQrcode() {
      this.clearTimer("wechat");
      this.qrInvalid = false;
      this.wxLoginUrl = "";
      this.wxUuid = this.generateUuid();
      getWxmpCode(this.wxUuid)
        .then((response) => {
          if (response.code !== 200)
            // 二维码获取失败
            throw new Error(response.msg || this.$t("auth.qrFetchFailed"));
          const payload =
            typeof response.msg === "string"
              ? JSON.parse(response.msg)
              : response.data;
          this.wxTicket = payload.ticket;
          this.wxLoginUrl = `https://mp.weixin.qq.com/cgi-bin/showqrcode?ticket=${payload.ticket}`;
          this.qrStartedAt = Date.now();
          this.startWechatPolling();
        })
        .catch((error) => {
          this.qrInvalid = true;
          this.$message.error(this.getErrorMessage(error));
        });
    },
    startWechatPolling() {
      this.clearTimer("wechat");
      this.timers.wechat = window.setInterval(this.pollWechatStatus, 3000);
    },
    async pollWechatStatus() {
      if (Date.now() - this.qrStartedAt >= 5 * 60 * 1000) {
        this.qrInvalid = true;
        this.clearTimer("wechat");
        return;
      }
      try {
        const response = await selectWxmpLoginStatus(
          this.wxUuid,
          this.wxTicket,
        );
        if (response.code !== 200) return;
        const data = response.data || {};
        if (data.token) {
          if (!this.ensureAgreement()) {
            this.clearTimer("wechat");
            return;
          }
          this.clearTimer("wechat");
          if (this.wechatPurpose === "register") {
            const registration = {
              channel: "wechat",
              region: this.registerRegion,
              accountType: "personal",
              account: "",
              displayName: "",
              companyName: "",
              contactName: "",
              verificationStatus: "not_required",
              next: "workspace",
            };
            this.$emit("registration-complete", registration);
            await this.applyLoginResult(
              data,
              // 微信注册成功
              this.$t("auth.wechatRegistrationSuccess"),
            );
          } else {
            await this.applyLoginResult(data);
          }
        }
      } catch (error) {
        // 轮询中的短暂网络错误不打断扫码体验。
      }
    },
    refreshQrcode() {
      this.loadWechatQrcode();
    },
    async sendRegisterCode() {
      if (this.registerChannel === "email") {
        if (!this.isValidEmail(this.registerForm.email)) {
          // 请输入正确的邮箱
          this.$message.warning(this.$t("auth.invalidEmail"));
          return;
        }
        try {
          const response = await sendEmailCode(this.registerForm.email);
          if (response.code !== 200) {
            // 邮箱验证码发送失败，请稍后重试
            this.$message.error(
              // 邮箱验证码发送失败，请稍后重试
              response.msg || this.$t("auth.emailCodeSendFailed"),
            );
            return;
          }
          this.startCountdown("register");
          // 邮箱验证码发送成功
          this.$message.success(this.$t("auth.emailCodeSent"));
        } catch (error) {
          this.$message.error(this.getErrorMessage(error));
        }
        return;
      }
      if (
        !this.isValidMobile(
          this.registerForm.mobile,
          this.registerForm.countryCode,
        )
      ) {
        this.$message.warning(
          this.registerForm.countryCode === "+86"
            ? // 请输入正确的中国大陆手机号
              this.$t("auth.invalidMainlandMobile")
            : this.registerForm.countryCode === "+852"
            ? // 请输入正确的中国香港手机号（8位数字）
              this.$t("auth.invalidHongKongMobile")
            : // 请输入6-20位数字手机号
              this.$t("auth.invalidOverseasMobile"),
        );
        return;
      }
      try {
        const response = await getMsgCode(
          this.registerForm.mobile,
          this.registerForm.countryCode,
          "register",
        );
        // 验证码发送失败
        if (response.code !== 200)
          throw new Error(
            // 验证码发送失败
            response.msg || this.$t("auth.verificationCodeSendFailed"),
          );
        this.startCountdown("register");
        // 短信验证码已发送
        this.$message.success(this.$t("auth.smsSent"));
      } catch (error) {
        this.$message.error(this.getErrorMessage(error));
      }
    },
    validateRegisterAccount() {
      if (!this.ensureAgreement()) return;
      let accountValid = false;
      if (this.registerChannel === "email") {
        accountValid = this.isValidEmail(this.registerForm.email);
      } else {
        accountValid = this.isValidMobile(
          this.registerForm.mobile,
          this.registerForm.countryCode,
        );
      }
      if (!accountValid) {
        this.$message.warning(
          this.registerChannel === "mobile"
            ? // 请输入正确的手机号
              // 请输入正确的手机号
              this.$t("auth.invalidMobile")
            : // 请输入正确的邮箱
              // 请输入正确的邮箱
              this.$t("auth.invalidEmail"),
        );
        return;
      }
      if (!/^\d{4}$/.test(this.registerForm.code)) {
        // 请输入4位数字验证码
        this.$message.warning(this.$t("auth.invalidFourDigitCode"));
        return;
      }
      this.submitRegistrationPassword();
    },
    async submitRegistrationPassword() {
      if (
        this.registerForm.password.length < 8 ||
        this.registerForm.password.length > 20
      ) {
        // 请输入 8–20 位登录密码
        this.$message.warning(this.$t("auth.invalidPasswordLength"));
        return;
      }
      if (this.registerAccountType === "company") {
        if (!this.registerForm.companyName) {
          // 请输入企业名称
          this.$message.warning(this.$t("auth.companyNameRequired"));
          return;
        }
        if (!this.registerForm.contactName) {
          // 请输入联系人姓名
          this.$message.warning(this.$t("auth.contactNameRequired"));
          return;
        }
      }
      if (this.submitting) return;

      const isCompany = this.registerAccountType === "company";
      const isMobile = this.registerChannel === "mobile";
      const payload = {
        registerType: isCompany ? "enterprise" : "personal",
        areaType: this.registerRegion === "mainland" ? 1 : 2,
        password: "",
        [isMobile ? "smsCode" : "emailCode"]: this.registerForm.code,
        companyName: isCompany ? this.registerForm.companyName : "",
        contactName: isCompany ? this.registerForm.contactName : "",
        role: "buyer",
        ...this.getReferralParams(),
      };
      if (isMobile) {
        payload.countryCode = this.registerForm.countryCode;
        payload.mobile = this.registerForm.mobile;
      } else {
        payload.email = this.registerForm.email;
      }

      try {
        this.submitting = true;
        payload.password = await encryptPassword(this.registerForm.password);
        const response = await registerUser(payload);
        if (response.code === 200) {
          this.registeredUser = response.data;
          this.clearReferralParams();
          this.registerStep = 3;
          this.scrollAuthPanelToTop();
        }
      } catch (error) {
        console.error(this.getErrorMessage(error));
      } finally {
        this.submitting = false;
      }
    },
    async finishRegistration(continueCertification) {
      const isCompany = this.registerAccountType === "company";
      if (!this.registeredUser) {
        // 注册信息已失效，请重新注册
        this.$message.error(this.$t("auth.registrationExpired"));
        return;
      }
      const registration = {
        channel: this.registerChannel,
        region: this.registerRegion,
        accountType: this.registerAccountType,
        account: this.registerAccountDisplay,
        displayName: this.registerForm.displayName,
        companyName: this.registerForm.companyName,
        contactName: this.registerForm.contactName,
        verificationStatus: isCompany ? "unverified" : "not_required",
        next:
          isCompany && continueCertification
            ? "enterprise-certification"
            : "workspace",
      };
      this.$emit("registration-complete", registration);
      if (isCompany && continueCertification) {
        this.$emit("enterprise-certification-requested", registration);
      }
      await this.applyLoginResult(
        this.registeredUser,
        // 注册成功，欢迎使用 Xsou
        this.$t("auth.registrationWelcome"),
      );
    },
    scrollAuthPanelToTop() {
      this.$nextTick(() => {
        const scrollContainer = this.$el.querySelector(".auth-account-scroll");
        if (scrollContainer) scrollContainer.scrollTop = 0;
      });
    },
    async handleGoogleLogin(response) {
      this.clearReferralParams();
      await this.applyLoginResult(response.data);
    },
    // 登录成功
    async applyLoginResult(
      data,
      // 登录成功
      successMessage = this.$t("auth.loginSuccess"),
    ) {
      this.$store.commit("user/setUserInfo", data);
      try {
        const response = await getUserInfoDetail();
        if (response.code === 200 && response.data) {
          this.$store.commit(
            "user/setUserDetails",
            response.data.userInfo || {},
          );
        }
      } catch (error) {
        // 登录 token 已写入，详情加载失败时仍允许用户继续使用。
      }
      // 登录成功
      this.$message.success(successMessage || this.$t("auth.loginSuccess"));
      this.$emit("login-success", data);
      this.closeDialog();
      this.$router.go(0);
    },
    parseQueryFromUrl(url) {
      if (!url) return {};
      try {
        const target = new URL(url, window.location.origin);
        return Object.fromEntries(target.searchParams.entries());
      } catch (error) {
        return {};
      }
    },
    getReferralParams() {
      const query = this.$route.query || {};
      const backQuery = this.parseQueryFromUrl(query.backUrl);
      return {
        referrerId:
          query.referrerId ||
          backQuery.referrerId ||
          localStorage.getItem("xsou_referrer_id"),
        inviteCode:
          query.inviteCode ||
          backQuery.inviteCode ||
          localStorage.getItem("xsou_invite_code"),
      };
    },
    clearReferralParams() {
      localStorage.removeItem("xsou_referrer_id");
      localStorage.removeItem("xsou_invite_code");
    },
    clearTimer(type) {
      if (!this.timers[type]) return;
      window.clearInterval(this.timers[type]);
      this.timers[type] = null;
    },
    clearAllTimers() {
      Object.keys(this.timers).forEach(this.clearTimer);
    },
    getErrorMessage(error) {
      return (
        (error &&
          error.response &&
          error.response.data &&
          error.response.data.msg) ||
        (error && error.message) ||
        // 请求失败，请稍后重试
        this.$t("auth.requestFailed")
      );
    },
  },
};
</script>

<style lang="less">
.xsou-auth-dialog {
  --auth-blue: #4286f5;
  --auth-blue-dark: #3578e5;
  --auth-navy: #071b3a;
  --auth-text-primary: #1f2a44;
  --auth-text-default: #475467;
  --auth-text-muted: #8a98ad;
  --auth-border: #d9e2ef;
  margin-top: 50vh !important;
  transform: translateY(-50%);
  overflow: hidden !important;
  border-radius: 28px !important;
  background: transparent;
  box-shadow: 0 28px 90px rgba(8, 30, 65, 0.24);
  clip-path: inset(0 round 28px);
  isolation: isolate;

  .el-dialog__header {
    display: none;
  }
  .el-dialog__body {
    overflow: hidden;
    padding: 0;
    border-radius: inherit;
  }

  * {
    box-sizing: border-box;
  }

  button,
  input,
  select {
    font: inherit;
  }

  button {
    -webkit-tap-highlight-color: transparent;
  }
}

.v-modal {
  backdrop-filter: blur(4px);
}

.auth-dialog-shell {
  position: relative;
  display: flex;
  width: 100%;
  height: min(650px, 84vh);
  min-height: 570px;
  overflow: hidden;
  background: #f3f4f6 url("~@/assets/bg/login.png") center center / cover
    no-repeat;
  border-radius: 28px;
}

.auth-dialog-shell::before {
  position: absolute;
  z-index: 0;
  inset: 0;
  border-radius: inherit;
  background: linear-gradient(
      180deg,
      rgba(9, 27, 52, 0.08) 0%,
      rgba(8, 28, 58, 0.16) 42%,
      rgba(7, 27, 56, 0.5) 72%,
      rgba(7, 24, 50, 0.88) 100%
    ),
    linear-gradient(108deg, rgba(20, 66, 129, 0.08), rgba(4, 15, 34, 0.28));
  content: "";
  pointer-events: none;
}

.auth-brand-panel {
  position: relative;
  z-index: 1;
  flex: 0 0 42%;
  overflow: hidden;
  border-radius: 28px;
  color: #fff;
  background: transparent;
}

.auth-brand-scene,
.auth-brand-shade {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
}

.auth-brand-scene {
  display: none;
}

.auth-brand-shade {
  display: none;
}

.auth-brand-content {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 36px 34px 34px;
}

.auth-brand-logo {
  width: 104px;
  height: 40px;
  margin-top: 8px;
  object-fit: cover;
  object-position: center;
  filter: brightness(0) invert(1);
}

.auth-dialog-shell[class*="auth-festival-"]
  .auth-brand-logo.festival-auth-logo {
  width: min(250px, 92%);
  height: 74px;
  object-fit: contain;
  object-position: left center;
  filter: drop-shadow(0 5px 13px rgba(11, 29, 57, 0.22));
}
.auth-dialog-shell.auth-festival-mid-autumn {
  background: radial-gradient(
      circle at 25% 18%,
      rgba(255, 247, 202, 0.98),
      transparent 30%
    ),
    linear-gradient(135deg, #173d83 0%, #315dab 42%, #f3bd4d 100%);
}
.auth-dialog-shell.auth-festival-national-day {
  background: radial-gradient(
      circle at 22% 18%,
      rgba(255, 225, 111, 0.9),
      transparent 30%
    ),
    linear-gradient(135deg, #940d16 0%, #d9252d 48%, #f2b632 100%);
}
.auth-dialog-shell[class*="auth-festival-"]::before {
  background: radial-gradient(
      circle at 14% 24%,
      rgba(255, 255, 255, 0.2) 0 2px,
      transparent 3px
    ),
    radial-gradient(
      circle at 34% 14%,
      rgba(255, 221, 109, 0.7) 0 2px,
      transparent 3px
    ),
    linear-gradient(180deg, rgba(7, 20, 43, 0.03), rgba(7, 20, 43, 0.62));
  background-size: 72px 72px, 95px 95px, auto;
}

.auth-brand-copy {
  margin-top: auto;
  margin-bottom: 58px;
}
.auth-brand-copy h2 {
  margin: 0;
  font-size: 27px;
  line-height: 1.42;
  letter-spacing: -0.8px;
}

.auth-brand-values {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 9px 15px;
  margin: 16px 0 0;
  color: rgba(255, 255, 255, 0.74);
  font-size: 13px;
  line-height: 1.7;
  letter-spacing: 0.2px;
}

.auth-brand-values span {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
}

.auth-brand-values span::before {
  width: 5px;
  height: 5px;
  flex: 0 0 5px;
  border-radius: 50%;
  background: #5b9bff;
  box-shadow: 0 0 0 3px rgba(91, 155, 255, 0.16),
    0 0 8px rgba(91, 155, 255, 0.42);
  content: "";
}

.auth-account-panel {
  --auth-tabs-height: 48px;

  .google-login {
    position: relative;
    z-index: 1;
    flex-shrink: 0;
  }

  position: relative;
  z-index: 1;
  display: flex;
  flex: 1;
  min-width: 0;
  flex-direction: column;
  margin: 14px;
  padding: 0 40px 20px;
  overflow: hidden;
  isolation: isolate;
  border: 0;
  border-radius: 20px;
  background: transparent;
  box-shadow: none;

  &::before {
    position: absolute;
    z-index: 0;
    top: var(--auth-tabs-height);
    right: 0;
    bottom: 0;
    left: 0;
    border-radius: 0 20px 0 0;
    background: #fff;
    content: "";
    pointer-events: none;
  }

  &.is-registering::before {
    border-radius: 20px 0 0 0;
  }

  .auth-close {
    position: absolute;
    z-index: 5;
    top: calc((var(--auth-tabs-height) - 30px) / 2);
    right: 18px;
    width: 30px;
    height: 30px;
    padding: 0;
    border: 0;
    border-radius: 50%;
    background: rgba(244, 246, 249, 0.9);
    cursor: pointer;
    transition: 0.2s ease;

    &:hover {
      background: #e9edf3;
    }

    span {
      position: absolute;
      top: 14px;
      left: 9px;
      width: 12px;
      height: 1.5px;
      background: #667085;

      &:first-child {
        transform: rotate(45deg);
      }

      &:last-child {
        transform: rotate(-45deg);
      }
    }
  }

  .auth-account-header {
    position: relative;
    z-index: 2;
    flex: 0 0 auto;
    height: var(--auth-tabs-height);
    margin: 0 -40px;
    clip-path: inset(0 0 -20px 0);
  }

  .auth-primary-tabs {
    position: relative;
    z-index: 1;
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    height: 100%;
    overflow: visible;
    isolation: isolate;
    border-radius: 20px 20px 0 0;

    &__card {
      position: absolute;
      z-index: 0;
      inset: 0;
      display: block;
      width: 100%;
      height: 100%;
      overflow: visible;
      pointer-events: none;
      opacity: 1;
      transition: none;

      path {
        fill: #d8d8d8;
        transition: none;
      }

      &.active {
        z-index: 1;
        animation: authCardLift 0.18s cubic-bezier(0.22, 1, 0.36, 1);

        path {
          fill: #fff;
        }
      }
    }

    &__item {
      position: relative;
      z-index: 3;
      min-width: 0;
      padding: 0;
      border: 0;
      outline: 0;
      background: transparent;
      color: #667085;
      font-size: 17px;
      font-weight: 500;
      cursor: pointer;
      transition: none;

      &:first-of-type {
        padding-left: 12px;
      }

      &:last-of-type {
        padding-right: 12px;
      }

      &:not(.active) {
        color: #667085;
      }

      &.active {
        color: var(--auth-text-primary);
        font-weight: 600;
        animation: authCardLift 0.18s cubic-bezier(0.22, 1, 0.36, 1);
      }

      &:focus-visible {
        box-shadow: inset 0 0 0 2px rgba(66, 134, 245, 0.45);
      }
    }

    @media (prefers-reduced-motion: reduce) {
      &__card.active,
      &__item.active {
        animation: none;
      }
    }
  }
}

@keyframes authCardLift {
  from {
    transform: translateY(4px);
  }
  to {
    transform: translateY(0);
  }
}

.auth-account-scroll {
  position: relative;
  z-index: 1;
  flex: 1;
  min-height: 0;
  margin: 22px -12px 0 0;
  padding-right: 12px;
  overflow-x: hidden;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: #d9e3f2 transparent;
}

.auth-account-scroll::-webkit-scrollbar {
  width: 4px;
}
.auth-account-scroll::-webkit-scrollbar-thumb {
  border-radius: 4px;
  background: #d9e3f2;
}
.auth-account-panel.is-registering .auth-account-scroll {
  margin-top: 24px;
}
.auth-register-view .auth-field {
  margin-bottom: 15px;
}
.auth-register-view .auth-field-label {
  margin-bottom: 7px;
}

.xsou-auth-dialog .auth-login-switcher {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 28px;
  margin-bottom: 20px;

  .auth-login-tabs {
    display: inline-flex;
    max-width: 100%;
    gap: 4px;
    padding: 4px;
    box-sizing: border-box;
    border-radius: 10px;
    background: #f4f6f9;

    button {
      appearance: none;
      min-height: 44px;
      padding: 8px 12px;
      border: 0;
      border-radius: 7px;
      background: transparent;
      color: var(--auth-text-default);
      font: inherit;
      font-size: 14px;
      line-height: 20px;
      white-space: nowrap;
      cursor: pointer;

      &:hover {
        color: var(--auth-blue-dark);
      }

      &.active {
        background: #fff;
        color: var(--auth-blue-dark);
        font-weight: 600;
        box-shadow: 0 1px 3px rgba(15, 23, 42, 0.08);
      }

      &:focus-visible {
        outline: 2px solid var(--auth-blue);
        outline-offset: 1px;
      }
    }
  }
}

.auth-login-view {
  display: flex;
  height: 100%;
  min-height: 0;
  flex-direction: column;
}

.auth-login-view > .auth-form {
  display: flex;
  flex: 1;
  min-height: 0;
  flex-direction: column;
}

.auth-login-title h3 {
  margin: 0;
  color: var(--auth-text-primary);
  font-size: 16px;
  font-weight: 600;
}

.auth-login-icon-actions {
  display: grid;
  grid-template-columns: repeat(2, max-content);
  align-items: center;
  justify-content: center;
  gap: 12px;

  > button {
    width: 100%;
  }

  > .google-login {
    justify-self: center;
  }
}

.auth-alt-login {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin: auto 0;
  padding: 18px 0;
}

.auth-login-icon-actions > button {
  box-sizing: border-box;
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 108px;
  height: 40px;
  gap: 7px;
  padding: 0 14px;
  border: 1px solid #d7e1ef;
  border-radius: 4px;
  color: #5968c8;
  background: #fff;
  box-shadow: none;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: 0.2s ease;
}

.auth-login-icon-actions > button:hover {
  border-color: #b9c2ef;
  background: #f5f6ff;
}

.auth-login-icon-actions > button.active {
  color: #fff;
  border-color: #5968c8;
  background: #5968c8;
  box-shadow: 0 5px 13px rgba(89, 104, 200, 0.2);
}

.auth-login-icon-actions .auth-wechat-action {
  border-color: #cfe8d8;
  color: #169b4c;
  background: #fbfffc;
}

.auth-login-icon-actions .auth-wechat-action:hover {
  border-color: #9dd5b0;
  color: #12833f;
  background: #f2fbf5;
}

.auth-login-icon-actions > button > svg {
  width: 19px;
  height: 19px;
  overflow: visible;
  fill: none;
  stroke: currentColor;
  stroke-width: 1.65;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.auth-login-icon-actions .auth-icon-fill {
  fill: currentColor;
  stroke: none;
}
.auth-form {
  display: block;
}
.auth-field-label,
.auth-choice-label {
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 0 0 8px;
  color: var(--auth-text-default);
  font-size: 14px;
  font-weight: 500;
}

.auth-field-label small {
  color: #9aa8bb;
  font-weight: 400;
}
.auth-field {
  display: flex;
  align-items: center;
  width: 100%;
  height: 46px;
  margin-bottom: 16px;
  overflow: hidden;
  border: 1px solid var(--auth-border);
  border-radius: 10px;
  background: #fff;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.auth-field:hover {
  border-color: #c3cede;
}
.auth-field:focus-within {
  border-color: var(--auth-blue);
  box-shadow: 0 0 0 2px rgba(66, 134, 245, 0.07);
}

.auth-field input,
.auth-field select {
  width: 100%;
  height: 100%;
  padding: 0 14px;
  border: 0;
  outline: 0;
  color: var(--auth-text-primary);
  background: transparent;
  font-size: 15px;
  font-weight: 400;
}

.auth-field input::placeholder {
  color: #a8b2c3;
}
.auth-field select {
  cursor: pointer;
}
.auth-area-select {
  position: relative;
  flex: 0 0 150px;
  height: 100%;
  border-right: 1px solid #e5ebf3;
}

.auth-area-select::after {
  position: absolute;
  top: 50%;
  right: 14px;
  width: 7px;
  height: 7px;
  border-right: 1.5px solid #526783;
  border-bottom: 1.5px solid #526783;
  content: "";
  pointer-events: none;
  transform: translateY(-65%) rotate(45deg);
}

.auth-area-select select {
  width: 100%;
  padding: 0 38px 0 16px;
  appearance: none;
  text-overflow: ellipsis;
}

.auth-area-select.auth-compact-area-select {
  flex: 0 0 72px;
}

.auth-compact-area-value {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding-right: 13px;
  color: var(--auth-text-default);
  font-size: 14px;
  font-weight: 500;
  pointer-events: none;
}

.auth-area-select.auth-compact-area-select::after {
  right: 9px;
  z-index: 2;
}

.auth-area-select.auth-compact-area-select select {
  position: absolute;
  inset: 0;
  z-index: 1;
  height: 100%;
  padding: 0;
  opacity: 0;
}
.auth-phone-field input {
  flex: 1;
  min-width: 0;
}
.auth-code-field input {
  flex: 1;
  min-width: 0;
}
.auth-code-field button,
.auth-password-field button {
  flex: 0 0 auto;
  height: 100%;
  border: 0;
  background: transparent;
  cursor: pointer;
}

.auth-code-field button {
  padding: 0 14px;
  color: var(--auth-blue);
  font-size: 12px;
  font-weight: 500;
}

.auth-password-field button {
  display: flex;
  width: 44px;
  align-items: center;
  justify-content: center;
  padding: 0;
  color: #71839a;
}

.auth-password-field button:hover {
  color: var(--auth-blue);
}
.auth-password-field button svg {
  width: 18px;
  height: 18px;
  fill: none;
  stroke: currentColor;
  stroke-width: 1.8;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.auth-code-field button:disabled {
  color: #a8b3c3;
  cursor: not-allowed;
}
.auth-primary-button,
.auth-secondary-button {
  width: 100%;
  height: 44px;
  border: 0;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: 0.2s ease;
}

.auth-primary-button {
  color: #fff;
  background: var(--auth-blue);
  box-shadow: 0 4px 12px rgba(66, 134, 245, 0.14);
}

.auth-primary-button:hover {
  background: var(--auth-blue-dark);
  box-shadow: 0 5px 14px rgba(66, 134, 245, 0.18);
}
.auth-primary-button:disabled {
  opacity: 0.65;
  cursor: wait;
  transform: none;
}
.auth-secondary-button {
  color: #5c6c83;
  border: 1px solid #dfe7f1;
  background: #fff;
}
.auth-form-switch {
  margin: 15px 0 0;
  text-align: center;
  color: #94a3b8;
  font-size: 12px;
}
.auth-sms-register-link {
  margin: 11px 0 0;
  text-align: center;
}
.auth-return-sms {
  padding: 0;
  border: 0;
  color: var(--auth-blue);
  background: transparent;
  cursor: pointer;
}

.auth-form-switch button,
.auth-text-button,
.auth-later-button {
  padding: 0;
  border: 0;
  color: var(--auth-blue);
  background: transparent;
  cursor: pointer;
}

.auth-label-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.auth-text-button {
  margin-bottom: 8px;
  font-size: 12px;
}
.auth-wechat-view {
  min-height: 350px;
  text-align: center;
}
.auth-qr-frame {
  position: relative;
  width: 190px;
  height: 190px;
  margin: 10px auto 17px;
  padding: 11px;
  border: 1px solid #e4eaf2;
  border-radius: 16px;
  background: #fff;
  box-shadow: 0 12px 30px rgba(30, 65, 115, 0.09);
}

.auth-qr-frame img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}
.auth-qr-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #94a3b8;
  font-size: 12px;
}
.auth-qr-loading span {
  width: 26px;
  height: 26px;
  margin-bottom: 12px;
  border: 2px solid #dbe5f2;
  border-top-color: var(--auth-blue);
  border-radius: 50%;
  animation: authSpin 0.8s linear infinite;
}
@keyframes authSpin {
  to {
    transform: rotate(360deg);
  }
}
.auth-qr-invalid {
  position: absolute;
  inset: 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  color: #475569;
  background: rgba(255, 255, 255, 0.94);
}
.auth-qr-invalid p {
  margin: 0 0 10px;
}
.auth-qr-invalid button {
  border: 0;
  color: var(--auth-blue);
  background: transparent;
  cursor: pointer;
}
.auth-wechat-view > h3 {
  margin: 0;
  color: #172b4d;
  font-size: 16px;
}
.auth-wechat-view > p {
  margin: 9px 0 14px;
  color: #94a3b8;
  font-size: 12px;
}
.auth-return-sms {
  display: block;
  margin: 12px auto 0;
  font-size: 11px;
}
.auth-return-sms:hover {
  color: var(--auth-blue-dark);
}
.auth-register-type-section {
  margin-bottom: 16px;

  .auth-register-account-control {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;

    > button {
      position: relative;
      display: flex;
      align-items: center;
      gap: 12px;
      min-width: 0;
      min-height: 60px;
      padding: 14px 28px 14px 16px;
      border: 1px solid var(--auth-border);
      border-radius: 12px;
      color: var(--auth-text-default);
      background: #fff;
      text-align: left;
      cursor: pointer;

      &:hover {
        border-color: #9ebeed;
      }

      &:focus-visible {
        outline: 2px solid var(--auth-blue);
        outline-offset: 3px;
      }

      &.active {
        border-color: var(--auth-blue);
        color: #315b9f;
        background: #f4f8ff;
      }

      .auth-register-account-icon {
        flex: 0 0 22px;
        width: 22px;
        height: 22px;
        fill: none;
        stroke: currentColor;
        stroke-width: 1.6;
        stroke-linecap: round;
        stroke-linejoin: round;
      }

      .auth-register-account-copy {
        display: flex;
        min-width: 0;
        flex-direction: column;

        b {
          font-size: 14px;
          font-weight: 600;
          line-height: 1.3;
        }
      }

      @media (max-width: 720px) {
        gap: 8px;
        padding-left: 12px;

        .auth-register-account-icon {
          flex-basis: 20px;
          width: 20px;
          height: 20px;
        }
      }
    }
  }
}

.auth-register-region-section {
  margin-bottom: 20px;

  .auth-register-region-select {
    display: flex;
    width: calc((100% - 12px) / 2);
    flex-direction: column;
    align-items: stretch;
    gap: 7px;
    color: var(--auth-text-default);
    font-size: 14px;

    > label {
      flex-shrink: 0;
      font-weight: 500;
    }

    .auth-register-region-select-control {
      display: block;
      width: 100%;
      min-width: 0;

      .el-input {
        .el-input__inner {
          height: 46px;
          padding: 0 38px 0 14px;
          border: 1px solid var(--auth-border);
          border-radius: 10px;
          color: var(--auth-text-primary);
          background: #fff;
          font-size: 15px;
          line-height: 46px;
          transition: border-color 0.15s ease, box-shadow 0.15s ease;

          &:hover {
            border-color: #b5c7e2;
          }

          &:focus {
            border-color: var(--auth-blue);
            box-shadow: 0 0 0 2px rgba(66, 134, 245, 0.08);
          }
        }

        .el-input__suffix {
          right: 12px;

          .el-select__caret {
            color: #8291a6;
            font-size: 14px;
            line-height: 46px;
          }
        }

        &.is-focus .el-input__inner {
          border-color: var(--auth-blue);
          box-shadow: 0 0 0 2px rgba(66, 134, 245, 0.08);
        }
      }
    }

    @media (max-width: 720px) {
      width: 100%;
    }
  }
}

.xsou-auth-region-dropdown.el-select-dropdown {
  margin-top: 6px;
  padding: 4px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(31, 42, 68, 0.12);

  .popper__arrow {
    display: none;
  }

  .el-select-dropdown__list {
    padding: 0;

    .el-select-dropdown__item {
      position: relative;
      display: flex;
      height: auto;
      min-height: 40px;
      flex-direction: column;
      justify-content: center;
      padding: 9px 32px 9px 12px;
      border-radius: 7px;
      color: #475467;
      font-size: 14px;
      line-height: 1.35;

      &.hover,
      &:hover {
        background: #f5f8fd;
      }

      &.selected {
        color: #3578e5;
        background: #edf4ff;
        font-weight: 500;

        &::after {
          position: absolute;
          top: 50%;
          right: 12px;
          color: #4286f5;
          content: "✓";
          transform: translateY(-50%);
        }
      }
    }
  }
}

.auth-fixed-area-code {
  display: flex;
  flex: 0 0 72px;
  align-items: center;
  justify-content: center;
  height: 100%;
  border-right: 1px solid #e5ebf3;
  color: var(--auth-text-default);
  font-size: 14px;
  font-weight: 500;
}

.auth-register-verification-fields {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.auth-company-fields {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.auth-register-password-block {
  display: block;
}
.auth-register-view .auth-primary-button {
  margin-top: 4px;
}
.auth-register-method-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-top: 14px;

  > button {
    box-sizing: border-box;
    margin: 0;
    display: inline-flex;
    min-width: 110px;
    height: 40px;
    align-items: center;
    justify-content: center;
    gap: 7px;
    padding: 0 13px;
    border: 1px solid #d8e0ef;
    border-radius: 4px;
    color: #5968c8;
    background: #fff;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: border-color 0.18s ease, color 0.18s ease, background 0.18s ease;

    &:hover {
      border-color: #b9c5ec;
      background: #f7f8ff;
    }

    > svg {
      width: 18px;
      height: 18px;
      fill: none;
      stroke: currentColor;
      stroke-width: 1.65;
      stroke-linecap: round;
      stroke-linejoin: round;

      .auth-icon-fill {
        fill: currentColor;
        stroke: none;
      }
    }

    &.auth-register-wechat-action {
      border-color: #cde7d6;
      color: #169b4c;
      background: #fbfffc;

      &:hover {
        border-color: #9fd4af;
        color: #12823f;
        background: #f3fbf5;
      }
    }
  }
}

.auth-register-wechat-view {
  min-height: 316px;
  padding-top: 2px;
  text-align: center;
}

.auth-register-wechat-view .auth-qr-frame {
  width: 176px;
  height: 176px;
  margin-top: 0;
  margin-bottom: 15px;
}

.auth-register-wechat-view h3 {
  margin: 0;
  color: var(--auth-text-primary);
  font-size: 16px;
  font-weight: 600;
}

.auth-register-wechat-view > p {
  margin: 8px 0 0;
  color: var(--auth-text-muted);
  font-size: 12px;
}

.auth-return-register-default {
  display: block;
  margin: 12px auto 0;
  padding: 0;
  border: 0;
  color: var(--auth-blue);
  background: transparent;
  font-size: 12px;
  cursor: pointer;
}

.auth-return-register-default:hover {
  color: var(--auth-blue-dark);
}
.auth-register-login-link {
  margin: 11px 0 0;
  color: var(--auth-text-muted);
  font-size: 12px;
  text-align: center;
}

.auth-register-login-link button {
  padding: 0;
  border: 0;
  color: var(--auth-blue);
  background: transparent;
  cursor: pointer;
}

.auth-register-success {
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 420px;
  margin: 55px auto 0;
  text-align: center;
}

.auth-success-mark {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 52px;
  height: 52px;
  border-radius: 50%;
  color: #fff;
  background: linear-gradient(135deg, var(--auth-blue), var(--auth-blue-dark));
  box-shadow: 0 10px 24px rgba(66, 134, 245, 0.22);
  font-size: 24px;
  font-weight: 700;
}

.auth-register-success > span {
  margin-top: 14px;
  color: var(--auth-blue);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 1.5px;
}

.auth-register-success h3 {
  margin: 6px 0 8px;
  color: #142847;
  font-size: 20px;
}

.auth-register-success > p {
  margin: 0 0 26px;
  color: #8a99ad;
  font-size: 12px;
  line-height: 1.7;
}

.auth-success-later {
  margin-top: 14px;
  font-size: 12px;
}

.auth-account-footer {
  position: relative;
  z-index: 1;
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  gap: 7px;
  min-height: 32px;
  margin-top: 26px;
  padding-top: 12px;
  border-top: 1px solid #eef2f7;
}

.auth-account-footer p {
  margin: 0;
  color: var(--auth-text-muted);
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
}
.auth-account-footer a {
  color: var(--auth-blue);
  text-decoration: none;
}
.auth-agreement-check {
  position: relative;
  flex: 0 0 16px;
  width: 16px;
  height: 16px;
  cursor: pointer;
}
.auth-agreement-check input {
  position: absolute;
  opacity: 0;
}
.auth-agreement-check span {
  display: block;
  width: 16px;
  height: 16px;
  border: 1px solid #bdc8d8;
  border-radius: 4px;
  background: #fff;
}
.auth-agreement-check input:checked + span {
  border-color: var(--auth-blue);
  background: var(--auth-blue);
}
.auth-agreement-check input:checked + span::after {
  position: absolute;
  top: 2px;
  left: 5.5px;
  width: 4px;
  height: 8px;
  border: solid #fff;
  border-width: 0 1.5px 1.5px 0;
  content: "";
  transform: rotate(45deg);
}

@media (max-width: 1024px) {
  .xsou-auth-dialog {
    width: calc(100vw - 40px) !important;
  }
  .auth-brand-panel {
    flex-basis: 38%;
  }
  .auth-account-panel {
    padding-right: 30px;
    padding-left: 30px;
  }
  .auth-brand-content {
    padding-right: 26px;
    padding-left: 26px;
  }
}

@media (max-width: 720px) {
  .xsou-auth-dialog {
    width: calc(100vw - 24px) !important;
    margin-top: 50vh !important;
    border-radius: 18px;
    clip-path: inset(0 round 18px);
  }

  .auth-dialog-shell {
    display: block;
    height: min(760px, 92vh);
    min-height: 0;
    border-radius: 18px;
  }

  .auth-brand-panel {
    height: 150px;
    border-radius: 18px 18px 0 0;
  }
  .auth-brand-scene {
    top: 0;
    height: 100%;
    object-position: 43% center;
  }
  .auth-brand-content {
    padding: 20px 22px;
  }
  .auth-brand-logo {
    width: 80px;
    height: 32px;
    margin-top: 0;
  }
  .auth-dialog-shell[class*="auth-festival-"]
    .auth-brand-logo.festival-auth-logo {
    width: min(210px, 78vw);
    height: 58px;
  }
  .auth-brand-copy {
    margin: 17px 0 0;
  }
  .auth-brand-copy h2 {
    font-size: 17px;
    line-height: 1.3;
  }
  .auth-brand-values {
    margin-top: 7px;
    font-size: 10px;
  }
  .auth-account-panel {
    height: calc(100% - 150px);
    margin: 0;
    padding: 0 20px 15px;
    border: 0;
    border-radius: 18px 18px 0 0;
    transform: translateY(-12px);

    .auth-close {
      right: 14px;
    }

    .auth-account-header {
      margin: 0 -20px;
    }

    .auth-primary-tabs {
      border-radius: 18px 18px 0 0;

      &__item {
        font-size: 16px;
      }
    }
  }
  .auth-account-scroll {
    margin-top: 19px;
  }
  .auth-account-panel.is-registering .auth-account-scroll {
    margin-top: 19px;
  }
  .auth-form-grid {
    grid-template-columns: 1fr;
    gap: 0;
  }
  .auth-area-select {
    flex-basis: 124px;
  }
  .auth-area-select::after {
    right: 10px;
  }
  .auth-area-select select {
    padding-right: 28px;
    padding-left: 10px;
    font-size: 11px;
  }
  .auth-register-verification-fields,
  .auth-company-fields {
    grid-template-columns: 1fr;
    gap: 0;
  }
  .auth-register-method-actions {
    gap: 10px;
  }
  .auth-register-method-actions > button {
    min-width: 0;
    flex: 1;
  }
  .auth-register-success {
    margin-top: 28px;
  }
  .auth-account-footer {
    padding-bottom: 2px;
  }
}
</style>
<!-- 请输入登录密码 -->
<!-- 请输入 8–20 位密码 -->
